import React, { useState, useEffect, useMemo } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertTriangle, CheckCircle, ChevronDown, CircleDot, FileText, Gauge,
  Loader2, RotateCcw, Save, Activity, Thermometer, Zap, Fuel, Wind, Timer, Power,
  Wrench, Eye,
} from 'lucide-react';
import { Section } from './helpers';
import { TabLoader } from './TabLoader';
import ScanHistory from './ScanHistory';
import ScanConfidenceReport from './ScanConfidenceReport';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getLiveDataIcon = (pid) => {
  const map = { '0C': Gauge, '0D': Activity, '05': Thermometer, '11': Zap, '2F': Fuel, '0F': Wind, '1F': Timer };
  return map[pid] || Activity;
};

const OBDTab = ({
  canEdit, inspection, liveProgressData, dtcCodes,
  requestingRescan, requestObdRescan,
  onRefresh,
}) => {
  const [toggling, setToggling] = useState(false);
  const [obdEnabledLocal, setObdEnabledLocal] = useState(null);
  const obdEnabled = obdEnabledLocal !== null ? obdEnabledLocal : inspection?.obd_scanning_enabled !== false;
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiStale, setAiStale] = useState(false);
  const [expandedCode, setExpandedCode] = useState(null);
  const [savingReport, setSavingReport] = useState(false);

  // Sync local override when inspection prop changes (e.g., parent refresh)
  useEffect(() => {
    setObdEnabledLocal(null);
  }, [inspection?.obd_scanning_enabled]);

  useEffect(() => {
    if (!inspection?.obd_ai_analysis) return;
    setAiAnalysis(inspection.obd_ai_analysis);
  }, [inspection?.obd_ai_analysis]);

  const triggerAiAnalysis = async () => {
    if (!inspection?.id) return;
    setAiAnalyzing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/inspections/${inspection.id}/obd-ai-analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setAiAnalysis({
          codes_for_report: res.data.codes_for_report,
          real_codes: res.data.real_codes || [],
          likely_codes: res.data.likely_codes || [],
          noise_codes: (res.data.noise_codes || []).concat(
            (res.data.filtered_out_codes || []).map(c => typeof c === 'string' ? { code: c, ai_verdict: 'NOISE' } : c)
          ),
          summary: res.data.summary,
          analyzed_at: new Date().toISOString(),
          source: 'manual_trigger',
        });
        const s = res.data.summary || {};
        toast.success(`AI Analysis complete: ${s.total || s.real_count || 0} verified code${(s.total || s.real_count || 0) !== 1 ? 's' : ''}`);
        setAiStale(false);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI analysis failed');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleToggleOBD = async (newValue) => {
    if (!inspection?.id) return;
    setToggling(true);
    setObdEnabledLocal(newValue); // Optimistic update
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/inspections/${inspection.id}/obd-scanning`,
        { enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`OBD Scanning ${newValue ? 'enabled' : 'disabled'}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      setObdEnabledLocal(!newValue); // Revert on failure
      toast.error(error.response?.data?.detail || 'Failed to toggle OBD scanning');
    } finally {
      setToggling(false);
    }
  };

  const handleSaveForReport = async () => {
    if (!inspection?.id || verifiedCodes.length === 0) return;
    setSavingReport(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/inspections/${inspection.id}/dtc-report-selection`,
        { selected_codes: verifiedCodes.map(c => c.code) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${verifiedCodes.length} DTC code${verifiedCodes.length !== 1 ? 's' : ''} saved for report`);
    } catch (err) {
      toast.error('Failed to save DTC selection');
    } finally {
      setSavingReport(false);
    }
  };

  // Build verified codes list — all codes from the analyzer are confirmed by the vehicle's ECU
  const verifiedCodes = useMemo(() => {
    if (!aiAnalysis) return [];
    return (aiAnalysis.codes_for_report || []).map(c => ({
      ...c,
      ai_severity: c.ai_severity === 'IMPORTANT' ? 'CRITICAL' : (c.ai_severity === 'CRITICAL' ? 'CRITICAL' : 'MINOR'),
      status_label: c.status === 'Pending' || c.status === 'pending' ? 'Pending'
        : c.status === 'Active' || c.status === 'active' || c.status === 'Confirmed' ? 'Active'
        : 'History',
    }));
  }, [aiAnalysis]);

  // Group verified codes by ECU system
  const groupedCodes = useMemo(() => {
    const groups = {};
    verifiedCodes.forEach(c => {
      const system = c.ecuSource || c.ecu_source || (c.code?.startsWith('P') ? 'Engine/Powertrain' : c.code?.startsWith('C') ? 'Chassis/ABS' : c.code?.startsWith('B') ? 'Body' : 'Network/Other');
      if (!groups[system]) groups[system] = { name: system, codes: [], critical: 0, minor: 0 };
      groups[system].codes.push(c);
      if (c.ai_severity === 'CRITICAL') groups[system].critical++;
      else groups[system].minor++;
    });
    return Object.values(groups).sort((a, b) => b.critical - a.critical);
  }, [verifiedCodes]);

  const [expandedGroups, setExpandedGroups] = useState(new Set());

  return (
    <>
      <TabsContent value="obd" className="space-y-3 mt-0">
        {!inspection?.id ? <TabLoader message="Loading OBD data..." /> : (<>
          {/* OBD Toggle */}
          <div className={`rounded-lg p-3 border-2 transition-colors ${obdEnabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid="obd-scanning-toggle-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${obdEnabled ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                  <Power className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">OBD Scanning</p>
                  <p className="text-xs text-gray-500">{obdEnabled ? 'Mechanic must complete OBD scan' : 'Mechanic can skip OBD scan'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {toggling && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                <Switch checked={obdEnabled} onCheckedChange={handleToggleOBD} disabled={toggling || !canEdit} data-testid="obd-scanning-toggle" />
              </div>
            </div>
          </div>

          {/* Scan Status Row */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <div className={`rounded-lg p-3 border ${liveProgressData?.obd_scan?.completed ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${liveProgressData?.obd_scan?.completed ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <Gauge className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900">{liveProgressData?.obd_scan?.completed ? 'Scan Done' : 'Pending'}</p>
                  {liveProgressData?.obd_scan?.scanned_at && (
                    <p className="text-[10px] text-gray-500">{new Date(liveProgressData.obd_scan.scanned_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
              {canEdit && (
                <Button variant={liveProgressData?.obd_scan?.completed ? "outline" : "default"} size="sm" onClick={requestObdRescan} disabled={requestingRescan}
                  className={`w-full mt-2 text-xs ${liveProgressData?.obd_scan?.completed ? "" : "bg-blue-600 hover:bg-blue-700"}`}>
                  {requestingRescan ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Requesting...</> : <><RotateCcw className="h-3 w-3 mr-1" />Rescan</>}
                </Button>
              )}
            </div>
            <div className={`lg:col-span-3 p-3 rounded-lg border flex items-center gap-2 ${dtcCodes.length > 0 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
              {dtcCodes.length > 0 ? (
                <><AlertTriangle className="h-5 w-5 text-blue-600" /><span className="text-sm text-blue-800 font-medium">{dtcCodes.length} DTC codes found</span></>
              ) : (
                <><CheckCircle className="h-5 w-5 text-green-500" /><span className="text-sm text-green-700">No DTC codes detected</span></>
              )}
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════
               DTC CODE ANALYSIS — SIMPLIFIED VIEW
             ════════════════════════════════════════════════════════ */}
          <Section title="DTC Code Analysis" icon={Zap} defaultOpen={true}
            badge={aiAnalysis ? `${verifiedCodes.length} verified` : 'Not analyzed'}>
            <div className="space-y-3">
              {/* Stale analysis warning */}
              {aiStale && aiAnalysis && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-300" data-testid="ai-stale-banner">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    <span>Report source changed. Re-run analysis to update.</span>
                  </div>
                  <Button variant="default" size="sm" onClick={triggerAiAnalysis} disabled={aiAnalyzing}
                    className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ai-reanalyze-stale-btn">
                    {aiAnalyzing ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Analyzing...</> : <><Zap className="h-3.5 w-3.5 mr-1.5" />Re-analyze</>}
                  </Button>
                </div>
              )}

              {/* Trigger / Status bar */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="text-sm text-gray-600">
                  {aiAnalysis ? (
                    <span>Analyzed: {new Date(aiAnalysis.analyzed_at).toLocaleString()} ({aiAnalysis.source?.includes('auto') ? 'Auto' : 'Manual'}{aiAnalysis.scan_number ? ` | Scan #${aiAnalysis.scan_number}` : ''})</span>
                  ) : dtcCodes.length > 0 ? (
                    <span>Run analysis to classify {dtcCodes.length} raw codes</span>
                  ) : (
                    <span>No codes to analyze</span>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={triggerAiAnalysis} disabled={aiAnalyzing || !dtcCodes.length}
                  data-testid="ai-analyze-btn" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                  {aiAnalyzing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing...</> : <><Zap className="h-4 w-4 mr-2" />{aiAnalysis ? 'Re-analyze' : 'Analyze Codes'}</>}
                </Button>
              </div>

              {/* Verified Codes List */}
              {aiAnalysis && (
                <>
                  {verifiedCodes.length > 0 ? (
                    <div className="space-y-2" data-testid="verified-codes-list">
                      {groupedCodes.map((group, gi) => (
                        <div key={gi} className="border rounded-lg overflow-hidden">
                          <div className="bg-slate-100 px-3 py-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-200 transition-colors"
                            onClick={() => setExpandedGroups(prev => { const s = new Set(prev); s.has(group.name) ? s.delete(group.name) : s.add(group.name); return s; })}>
                            <div className="flex items-center gap-2.5">
                              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expandedGroups.has(group.name) ? '' : '-rotate-90'}`} />
                              <div className="h-8 w-8 rounded-lg bg-slate-700 flex items-center justify-center">
                                <span className="text-white font-bold text-[10px]">{group.name.substring(0, 4).toUpperCase()}</span>
                              </div>
                              <div>
                                <p className="font-semibold text-sm text-gray-900">{group.name}</p>
                                <p className="text-[11px] text-gray-500">{group.codes.length} code{group.codes.length !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              {group.critical > 0 && <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700 border border-red-200 font-medium">{group.critical} Critical</span>}
                              {group.minor > 0 && <span className="px-2 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-700 border border-sky-200 font-medium">{group.minor} Minor</span>}
                            </div>
                          </div>
                          {expandedGroups.has(group.name) && (
                            <div className="divide-y">
                              {group.codes.map((c, ci) => {
                                const isExpanded = expandedCode === c.code;
                                const hasCauses = c.possible_causes?.length > 0;
                                const hasSymptoms = c.symptoms?.length > 0;
                                const hasActions = c.recommended_actions?.length > 0;
                                const hasDetails = hasCauses || hasSymptoms || hasActions;
                                return (
                                  <div key={ci} className={`${ci % 2 ? 'bg-gray-50/50' : 'bg-white'}`} data-testid={`ai-code-${c.code}`}>
                                    <div className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
                                      onClick={() => setExpandedCode(isExpanded ? null : c.code)}>
                                      <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${c.ai_severity === 'CRITICAL' ? 'bg-red-100' : 'bg-sky-100'}`}>
                                        <CircleDot className={`h-3.5 w-3.5 ${c.ai_severity === 'CRITICAL' ? 'text-red-600' : 'text-sky-600'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="font-mono font-bold text-slate-800 text-sm">{c.code}</span>
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                            c.ai_severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700'
                                          }`}>{c.ai_severity}</span>
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                            c.status_label === 'Active' ? 'bg-red-50 text-red-600' : c.status_label === 'Pending' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'
                                          }`}>{c.status_label}</span>
                                          {hasDetails && (
                                            <Eye className={`h-3 w-3 ml-1 ${isExpanded ? 'text-blue-500' : 'text-gray-300'}`} />
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5 leading-snug">{c.ai_explanation || c.description || 'No description'}</p>
                                      </div>
                                    </div>
                                    {/* Expandable: Causes, Symptoms, Solutions */}
                                    {isExpanded && hasDetails && (
                                      <div className="px-3 pb-3 pt-0 ml-10 space-y-2">
                                        {hasCauses && (
                                          <DetailBlock title="Possible Causes" items={c.possible_causes} color="amber" icon={AlertTriangle} />
                                        )}
                                        {hasSymptoms && (
                                          <DetailBlock title="Symptoms" items={c.symptoms} color="blue" icon={Eye} />
                                        )}
                                        {hasActions && (
                                          <DetailBlock title="Recommended Actions" items={c.recommended_actions} color="emerald" icon={Wrench} />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 font-medium">No fault codes detected</p>
                      <p className="text-xs text-gray-400 mt-1">All codes were classified as noise or the vehicle is clean</p>
                    </div>
                  )}

                  {/* Save for Report */}
                  {verifiedCodes.length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div>
                        <p className="font-medium text-purple-900 text-sm">{verifiedCodes.length} code{verifiedCodes.length !== 1 ? 's' : ''} for customer report</p>
                        <p className="text-xs text-purple-600">These codes will appear in the inspection report</p>
                      </div>
                      <Button onClick={handleSaveForReport} disabled={savingReport} className="bg-purple-600 hover:bg-purple-700" data-testid="save-report-btn">
                        {savingReport ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save for Report</>}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Section>

          {/* Live Sensor Data */}
          {liveProgressData?.obd_scan?.data?.live_data && liveProgressData.obd_scan.data.live_data.length > 0 && (
            <Section title="Live Sensor Data" icon={Activity} defaultOpen={true} badge={`${liveProgressData.obd_scan.data.live_data.length} readings`}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="obd-live-data-grid">
                {liveProgressData.obd_scan.data.live_data.map((reading, idx) => {
                  const IconComp = getLiveDataIcon(reading.pid);
                  return (
                    <div key={idx} className="bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 hover:shadow-sm transition-all" data-testid={`live-data-reading-${reading.pid || idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-7 w-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <IconComp className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-500 font-medium truncate">{reading.name}</span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-gray-900">{typeof reading.value === 'number' ? reading.value.toFixed(reading.value % 1 === 0 ? 0 : 1) : reading.value}</span>
                        <span className="text-xs text-gray-400 font-medium">{reading.unit}</span>
                      </div>
                      {reading.pid && <span className="text-[10px] text-gray-400 font-mono mt-1 block">PID: {reading.pid}</span>}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Scan Confidence Report */}
          <ScanConfidenceReport inspectionId={inspection?.id} />

          {/* Scan History */}
          <ScanHistory inspectionId={inspection?.id} onActiveScanChanged={() => setAiStale(true)} />

          {/* Raw OBD Data */}
          {liveProgressData?.obd_scan?.data && (
            <Section title="Raw OBD Data" icon={FileText} badge="JSON">
              <div className="bg-gray-900 rounded-lg p-4 overflow-auto max-h-60">
                <pre className="text-xs text-green-400 font-mono">{JSON.stringify(liveProgressData.obd_scan.data, null, 2)}</pre>
              </div>
            </Section>
          )}
        </>)}
      </TabsContent>
    </>
  );
};

/* ── Detail Block for Causes/Symptoms/Actions ── */
const DETAIL_STYLES = {
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', title: 'text-amber-700', dot: 'bg-amber-400' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', title: 'text-blue-700', dot: 'bg-blue-400' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-500', title: 'text-emerald-700', dot: 'bg-emerald-400' },
};

const DetailBlock = ({ title, items, color, icon: Icon }) => {
  const s = DETAIL_STYLES[color] || DETAIL_STYLES.blue;
  return (
    <div className={`${s.bg} border ${s.border} rounded-lg p-2.5`} data-testid={`detail-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${s.icon}`} />
        <span className={`text-[11px] font-semibold ${s.title}`}>{title}</span>
      </div>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-snug">
            <span className={`${s.dot} h-1.5 w-1.5 rounded-full mt-1.5 flex-shrink-0`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default OBDTab;
